import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onChange,
      disabled = false,
      label,
      error,
      helperText,
      size = 'md',
      variant = 'default',
      className,
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const sizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };

    const variantClasses = {
      default: 'text-blue-600 focus:ring-blue-500 border-gray-300',
      primary: 'text-blue-600 focus:ring-blue-500 border-blue-300',
      success: 'text-green-600 focus:ring-green-500 border-green-300',
      warning: 'text-yellow-600 focus:ring-yellow-500 border-yellow-300',
      danger: 'text-red-600 focus:ring-red-500 border-red-300',
    };

    return (
      <div className={cn('space-y-1', className)}>
        <div className="flex items-start space-x-2">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className={cn(
              'rounded border focus:ring-2 focus:ring-offset-2 transition-colors duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              sizeClasses[size],
              variantClasses[variant],
              error && 'border-red-500 focus:ring-red-500',
              className
            )}
            aria-invalid={!!error}
            aria-describedby={
              error 
                ? `${checkboxId}-error` 
                : helperText 
                  ? `${checkboxId}-helper` 
                  : undefined
            }
            {...props}
          />
          {label && (
            <label
              htmlFor={checkboxId}
              className={cn(
                'text-sm text-gray-700 dark:text-gray-300 cursor-pointer',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {label}
            </label>
          )}
        </div>
        
        {(error || helperText) && (
          <div className="ml-6">
            {error && (
              <p 
                id={`${checkboxId}-error`}
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {error}
              </p>
            )}
            {helperText && !error && (
              <p 
                id={`${checkboxId}-helper`}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox'; 